import React, { createContext, useState, useContext, ReactNode, useEffect } from 'react';

type Language = 'zh' | 'vi';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  toggleLanguage: () => void;
  t: (key: string) => string;
}

const translations = {
  zh: {
    // 导航
    home: '首页',
    about: '关于我们',
    services: '业务范围',
    projects: '项目案例',
    contact: '联系我们',
    // 按钮
    learnMore: '了解更多',
    getQuote: '获取报价',
    contactUs: '联系我们',
    viewDetails: '查看详情',
    sendMessage: '发送消息',
    // 通用
    companyName: '惠亚建筑工程有限公司',
    slogan: '专业工业厂房建筑工程服务',
    allRightsReserved: '版权所有',
  },
  vi: {
    // 导航
    home: 'Trang chủ',
    about: 'Về chúng tôi',
    services: 'Dịch vụ',
    projects: 'Dự án',
    contact: 'Liên hệ',
    // 按钮
    learnMore: 'Tìm hiểu thêm',
    getQuote: 'Nhận báo giá',
    contactUs: 'Liên hệ chúng tôi',
    viewDetails: 'Xem chi tiết',
    sendMessage: 'Gửi tin nhắn',
    // 通用
    companyName: 'Công ty TNHH Xây dựng Hueya',
    slogan: 'Dịch vụ xây dựng nhà xưởng công nghiệp chuyên nghiệp',
    allRightsReserved: 'Bảo lưu mọi quyền',
  }
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const useLanguageContext = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguageContext must be used within a LanguageProvider');
  }
  return context;
};

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>('zh');

  useEffect(() => {
    const savedLang = localStorage.getItem('preferredLanguage') as Language;
    if (savedLang && (savedLang === 'zh' || savedLang === 'vi')) {
      setLanguageState(savedLang);
    }
  }, []);

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem('preferredLanguage', lang);
  };

  const toggleLanguage = () => {
    const newLang = language === 'zh' ? 'vi' : 'zh';
    setLanguage(newLang);
  };

  const t = (key: string): string => {
    const langTranslations = translations[language];
    return langTranslations[key as keyof typeof langTranslations] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};